import React, { useEffect, useMemo, useState } from 'react';
import { Input } from 'reactstrap';
import classNames from 'classnames';

import { useToast } from '~libs/toast';

import { AsyncActionIcon, Button, Modal, TabContainer, Toolbar } from '~components';
import { faFileExcel } from '~components/icon';
import { Column, Row, SortState, TableView } from '~components/table-view';

import { DASHBOARD_TEXT } from './dashboardText';

/** Typing waits this long before the table searches, with the table showing as pending meanwhile. */
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 25;

interface ComponentProps {
  isOpen: boolean;
  title: string;
  columns: Column[];
  /** Every row, in ranked order: searched, sorted and paged here with no api call. */
  rows: Row[];
  searchLabel: string;
  /** A narrower search box, leaving the toolbar room for filters, e.g. the workers table. Default full width. */
  compactSearch?: boolean;
  /** The text a search looks in, e.g. client name and budget reference. */
  searchOf: (row: Row) => string;
  /** The sort the ranked order already is, shown on its column header. */
  initialSortState: SortState;
  /** Views as tabs above the table, e.g. Most Hours First / Least Hours First; switching resets sort and page. */
  tabs?: { items: Array<{ key: string; label: string }>; activeKey: string; onChange: (key: string) => void };
  /** Extra controls in the toolbar after the search box, e.g. the Show Workers with No CM Hours checkbox. */
  filters?: React.ReactNode;
  /** Downloads the whole table (every row, whatever the search, sort or page) as xlsx; shows the export icon. */
  onExport?: () => Promise<void>;
  onClose: () => void;
}

const compareText = (a: string, b: string): number => a.localeCompare(b, 'en-AU', { sensitivity: 'base' });

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '';

/** One column compared: numbers numerically, text A to Z; null and empty always last, whichever way it sorts. */
const compareValues = (a: unknown, b: unknown, direction: 'asc' | 'desc'): number => {
  if (isBlank(a) || isBlank(b)) return Number(isBlank(a)) - Number(isBlank(b));
  const order = typeof a === 'number' && typeof b === 'number' ? a - b : compareText(String(a), String(b));
  return direction === 'asc' ? order : -order;
};

interface ExportIconProps {
  title: string;
  disabled: boolean;
  onExport: () => Promise<void>;
}

/** Export All to XLSX: disables and shows a spinner while the file is made, a toast when it fails. */
const ExportIcon: React.FC<ExportIconProps> = ({ title, disabled, onExport }) => {
  const { showToast } = useToast();

  return (
    <AsyncActionIcon
      id="dashboard-full-table-export"
      icon={faFileExcel}
      toolTip={DASHBOARD_TEXT.list.exportTooltip}
      ariaLabel={DASHBOARD_TEXT.list.exportLabel(title)}
      dataCy="dashboard-full-table-export-icon"
      spinnerDataCy="dashboard-full-table-export-spinner"
      disabled={disabled}
      onClick={onExport}
      onError={() => showToast('danger', DASHBOARD_TEXT.list.exportFailed)}
      successTitle={DASHBOARD_TEXT.list.exported}
    />
  );
};

/**
 * The full list behind a Dashboard widget's Show all (TAV-3463), in a modal. Search (debounced 300ms, pending shown),
 * column sort, page size and pages all run in the browser over the rows the page already has. Mount it with a key per
 * table so its search, sort and page start fresh each time.
 */
export const DashboardFullTable: React.FC<ComponentProps> = ({
  isOpen,
  title,
  columns,
  rows,
  searchLabel,
  compactSearch = false,
  searchOf,
  initialSortState,
  tabs,
  filters,
  onExport,
  onClose,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortState, setSortState] = useState<SortState>(initialSortState);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    if (searchInput === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  // Typing is waiting out the debounce: the table shows as pending before the search runs
  const pending = searchInput !== search;

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? rows.filter((row) => searchOf(row).toLowerCase().includes(needle)) : rows;
  }, [rows, search, searchOf]);

  const sorted = useMemo(() => {
    const [sort] = sortState;
    if (!sort?.direction) return filtered;
    const { id, direction } = sort;
    return [...filtered].sort((a, b) => compareValues(a[id], b[id], direction));
  }, [filtered, sortState]);

  const pageRows = useMemo(() => sorted.slice((page - 1) * pageSize, page * pageSize), [sorted, page, pageSize]);

  // Another view (tab) starts from its own order and the first page
  const activeTabKey = tabs?.activeKey;
  useEffect(() => {
    setSortState(initialSortState);
    setPage(1);
    // Only a switch of view resets them, not a new array of the same sort
  }, [activeTabKey]);

  const table = (
    <TableView
      key={activeTabKey}
      id="dashboard-full-table"
      data={pageRows}
      columns={columns}
      isLoading={pending}
      allowSort
      initialSortState={initialSortState}
      onSort={(next) => {
        setSortState(next);
        setPage(1);
      }}
      allowPagination
      paginationType="pages"
      showCount
      currentPage={page}
      pageSize={pageSize}
      totalCount={filtered.length}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPage(1);
      }}
      showPageSizeOptions
      readonly
      emptyMessage={search ? DASHBOARD_TEXT.list.noMatch : undefined}
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) onClose();
      }}
      header={title}
      size="xl"
      footer={
        <Button color="secondary" onClick={onClose}>
          {DASHBOARD_TEXT.list.close}
        </Button>
      }
    >
      <div>
        {/* Search and filters on the left, Export All to XLSX on the right, as the app's other toolbars */}
        <div className={classNames('dashboard-full-table-toolbar', { compact: compactSearch })}>
          {/* Its own id: the table below is "dashboard-full-table" */}
          <Toolbar
            id="dashboard-full-table-toolbar"
            leftCluster={
              <>
                <Input
                  type="search"
                  bsSize="sm"
                  aria-label={searchLabel}
                  placeholder={searchLabel}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  data-cy="dashboard-full-table-search-input"
                />
                {filters}
              </>
            }
            rightCluster={onExport && <ExportIcon title={title} disabled={rows.length === 0} onExport={onExport} />}
          />
        </div>
        {tabs ? (
          <TabContainer
            className="dashboard-full-table-tabs"
            setPageTitle={false}
            activeTab={Math.max(
              0,
              tabs.items.findIndex(({ key }) => key === tabs.activeKey),
            )}
            onTabChange={(index) => tabs.onChange(tabs.items[index].key)}
            tabs={tabs.items.map(({ key, label }) => ({
              id: `dashboard-full-table-${key}`,
              title: label,
              content: key === tabs.activeKey ? table : null,
            }))}
          />
        ) : (
          table
        )}
      </div>
    </Modal>
  );
};
